import { Page, PageRequest } from '$synapse/models';
import { GroupService } from '$synapse/services';
import {
  Component,
  ElementRef,
  OnInit,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  debounceTime,
  first,
  map,
  Observable,
  startWith,
  switchMap,
} from 'rxjs';
import { GroupSummary } from '$synapse/dtos';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { SHARED_IMPORTS } from '../../shared-imports';

@Component({
  selector: 'syn-group-picker',
  templateUrl: './group-picker.component.html',
  imports: [SHARED_IMPORTS],
})
export class GroupPickerComponent implements OnInit {
  private readonly _groupService = inject(GroupService);

  readonly candidateGroups = input<GroupSummary[]>([]);
  readonly addGroup = output<GroupSummary[]>();
  readonly groupInput = viewChild<ElementRef<HTMLInputElement>>('groupInput');

  groupControl = new UntypedFormControl('');
  _delayTimeMs = 300;
  filteredGroupsSummary: Observable<GroupSummary[]>;

  ngOnInit(): void {
    this.filteredGroupsSummary = this.groupControl.valueChanges.pipe(
      startWith(''),
      debounceTime(this._delayTimeMs),
      switchMap((group) => this._search(group)),
      map((page: Page<GroupSummary>) =>
        page.content.filter(
          (group) =>
            !this.candidateGroups()
              .map((group) => group.id)
              .includes(group.id)
        )
      )
    );
  }

  remove(GroupSummary: GroupSummary): void {
    const index = this.candidateGroups().indexOf(GroupSummary);

    if (index >= 0) {
      this.candidateGroups().splice(index, 1);
    }
    this.addGroup.emit(this.candidateGroups());
    this.groupControl.setValue(null);
  }

  groupSelected(event: MatAutocompleteSelectedEvent): void {
    const groupsSummary: GroupSummary = event.option.value;
    this.candidateGroups().push(groupsSummary);
    this.groupInput().nativeElement.value = '';
    this.groupControl.setValue(null);

    this.addGroup.emit(this.candidateGroups());
  }

  private _search(filter: string): Observable<Page<GroupSummary>> {
    const search = filter || '';

    let page: PageRequest = new PageRequest({
      search: `name~=${search},description~=${search}`,
    });
    return this._groupService.findAll(page).pipe(first());
  }
}
